import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Badge } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { categories } from '../data/categories';

// Fix for Leaflet marker icons
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [mapCenter] = useState([18.1096, -77.2975]); // Jamaica's coordinates

  // Perform search when component mounts or when search query changes
  React.useEffect(() => {
    if (initialQuery) {
      performSearch(initialQuery);
    }
  }, [initialQuery]);

  const performSearch = (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = [];
    const lowerCaseQuery = query.toLowerCase();

    // Search through all categories and their items
    categories.forEach(category => {
      category.subcategories.forEach(subcategory => {
        subcategory.items.forEach(item => {
          if (
            item.name.toLowerCase().includes(lowerCaseQuery) ||
            item.location.toLowerCase().includes(lowerCaseQuery) ||
            category.title.toLowerCase().includes(lowerCaseQuery) ||
            subcategory.title.toLowerCase().includes(lowerCaseQuery)
          ) {
            results.push({
              ...item,
              category: category.id,
              subcategory: subcategory.id,
              categoryTitle: category.title,
              subcategoryTitle: subcategory.title
            });
          }
        });
      });
    });

    setSearchResults(results);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(searchTerm);
  };

  // Generate random coordinates for Jamaica for demo purposes
  const getRandomJamaicaCoordinates = () => {
    return [
      18.1096 + (Math.random() - 0.5) * 0.5,
      -77.2975 + (Math.random() - 0.5) * 0.5
    ];
  };

  return (
    <Container className="py-5">
      <h1 className="mb-4">Search Results</h1>
      
      <Form className="mb-5" onSubmit={handleSearch}>
        <Row>
          <Col md={10}>
            <Form.Control
              type="search"
              placeholder="Search for anything in Jamaica..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="lg"
            />
          </Col>
          <Col md={2}>
            <Button variant="primary" type="submit" size="lg" className="w-100">
              Search
            </Button>
          </Col>
        </Row>
      </Form>

      {searchResults.length > 0 ? (
        <>
          <div className="mb-5">
            <h2 className="mb-4">Map View</h2>
            <div style={{ height: '400px', width: '100%', borderRadius: '10px', overflow: 'hidden' }}>
              <MapContainer 
                center={mapCenter} 
                zoom={8} 
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {searchResults.map((result, index) => (
                  <Marker 
                    key={`${result.category}-${result.id}-${index}`}
                    position={getRandomJamaicaCoordinates()}
                  >
                    <Popup>
                      <strong>{result.name}</strong><br />
                      {result.location}<br />
                      Rating: {result.rating}/5<br />
                      <Link to={`/listing/${result.category}/${result.subcategory}/${result.id}`}>
                        View Details
                      </Link>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>

          <h2 className="mb-4">Search Results ({searchResults.length})</h2>
          <Row>
            {searchResults.map((result, index) => (
              <Col key={`${result.category}-${result.id}-${index}`} md={4} className="mb-4">
                <Card className="h-100 shadow-sm hover-card">
                  <Card.Body>
                    <span className="badge bg-primary mb-2">{result.categoryTitle}</span>
                    <Card.Title>{result.name}</Card.Title>
                    <Card.Subtitle className="mb-2 text-muted">{result.location}</Card.Subtitle>
                    <div className="mb-2">
                      <Badge bg="success">Rating: {result.rating}/5</Badge>
                    </div>
                    <Card.Text>
                      {result.subcategoryTitle}
                    </Card.Text>
                    <Link 
                      to={`/listing/${result.category}/${result.subcategory}/${result.id}`}
                      className="btn btn-outline-primary"
                    >
                      View Details
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      ) : initialQuery ? (
        <div className="text-center py-5">
          <h3>No results found for "{initialQuery}"</h3>
          <p>Try different keywords or browse our categories below.</p>
          <Row className="mt-4">
            {categories.slice(0, 3).map((category) => (
              <Col key={category.id} md={4} className="mb-4">
                <Card className="h-100 shadow-sm hover-card">
                  <Card.Body>
                    <Card.Title>{category.title}</Card.Title>
                    <Card.Text>{category.description}</Card.Text>
                    <Link 
                      to={`/category/${category.id}`}
                      className="btn btn-outline-primary"
                    >
                      Explore {category.title}
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      ) : (
        <div className="text-center py-5">
          <h3>Enter a search term to find places in Jamaica</h3>
          <p>Search for historic sites, restaurants, pet services, marinas, golf courses, and more.</p>
        </div>
      )}
    </Container>
  );
};

export default SearchPage;
